import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { FileText, ShoppingCart, Receipt, Eye, Printer, Wrench, Camera, MessageSquare, User, Save } from "lucide-react";

const devisStatusLabels = { draft: "Brouillon", sent: "Envoyé", accepted: "Accepté", rejected: "Refusé" };
const devisStatusColors = {
  draft: "bg-slate-100 text-slate-600", sent: "bg-blue-50 text-blue-600",
  accepted: "bg-emerald-50 text-emerald-600", rejected: "bg-red-50 text-red-600"
};
const cmdStatusLabels = { pending: "En attente", in_progress: "En cours", completed: "Terminé", cancelled: "Annulé" };
const cmdStatusColors = {
  pending: "bg-amber-50 text-amber-600", in_progress: "bg-blue-50 text-blue-600",
  completed: "bg-emerald-50 text-emerald-600", cancelled: "bg-slate-100 text-slate-500"
};
const facStatusLabels = { emise: "Émise", payee: "Payée", annulee: "Annulée" };
const facStatusColors = {
  emise: "bg-blue-50 text-blue-600", payee: "bg-emerald-50 text-emerald-600", annulee: "bg-slate-100 text-slate-500"
};
const intStatusLabels = { planifie: "Planifié", en_cours: "En cours", termine: "Terminé", annule: "Annulé" };
const intStatusColors = {
  planifie: "bg-blue-50 text-blue-600", en_cours: "bg-amber-50 text-amber-600",
  termine: "bg-emerald-50 text-emerald-600", annule: "bg-slate-100 text-slate-500"
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EspaceClientPage() {
  const { apiFetch, API, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [devisList, setDevisList] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [factures, setFactures] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [devisDialogOpen, setDevisDialogOpen] = useState(false);
  const [factureDialogOpen, setFactureDialogOpen] = useState(false);
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);

  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, devisRes, cmdRes, facRes, intRes, profileRes] = await Promise.all([
        apiFetch(`${API}/client-portal/my-stats`),
        apiFetch(`${API}/client-portal/my-devis`),
        apiFetch(`${API}/client-portal/my-commandes`),
        apiFetch(`${API}/client-portal/my-factures`),
        apiFetch(`${API}/client-portal/my-interventions`),
        apiFetch(`${API}/client-portal/profile`)
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (devisRes.ok) setDevisList(await devisRes.json());
      if (cmdRes.ok) setCommandes(await cmdRes.json());
      if (facRes.ok) setFactures(await facRes.json());
      if (intRes.ok) setInterventions(await intRes.json());
      if (profileRes.ok) {
        const p = await profileRes.json();
        setProfile(p);
        setProfileForm(p);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await apiFetch(`${API}/client-portal/profile`, {
        method: "PUT",
        body: JSON.stringify(profileForm),
      });
      if (res.ok) {
        toast.success("Profil mis à jour");
        const updated = await res.json();
        setProfile(updated);
        setProfileForm(updated);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de sauvegarde"); }
    finally { setSavingProfile(false); }
  };

  const viewDevis = async (devisId) => {
    const res = await apiFetch(`${API}/client-portal/devis/${devisId}`);
    if (res.ok) {
      setSelectedDevis(await res.json());
      setDevisDialogOpen(true);
    }
  };

  const viewFacture = (facture) => {
    setSelectedFacture(facture);
    setFactureDialogOpen(true);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const printDocument = (elementId, title) => {
    const content = document.getElementById(elementId);
    if (!content) return;
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: Arial, sans-serif; color: #1e293b; }
        .header { background: #1a3a5c; color: white; padding: 30px; display: flex; justify-content: space-between; align-items: flex-start; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        thead tr { background: #1a3a5c; color: white; }
        th { padding: 10px 12px; font-size: 11px; font-weight: 600; text-align: left; }
        th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align: right; }
        td { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
        td:nth-child(2), td:nth-child(3), td:nth-child(4) { text-align: right; }
        tr:nth-child(even) { background: #f8fafc; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-28 bg-slate-200 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="espace-client-page">
      <div className="mb-6">
        <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Mon Espace Client</h1>
        <p className="text-sm text-slate-500 mt-1">
          Bienvenue {user?.name} — Retrouvez tous vos documents ci-dessous
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wider">Devis</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 font-barlow">{stats?.total_devis || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-md flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wider">Commandes</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 font-barlow">{stats?.total_commandes || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-50 rounded-md flex items-center justify-center shrink-0">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wider">Factures</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-0.5 font-barlow">{stats?.total_factures || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-50 rounded-md flex items-center justify-center shrink-0">
                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabsList className="mb-4 w-max">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="devis" data-testid="tab-devis" className="text-xs sm:text-sm">Devis ({devisList.length})</TabsTrigger>
            <TabsTrigger value="commandes" data-testid="tab-commandes" className="text-xs sm:text-sm">Commandes ({commandes.length})</TabsTrigger>
            <TabsTrigger value="factures" data-testid="tab-factures" className="text-xs sm:text-sm">Factures ({factures.length})</TabsTrigger>
            <TabsTrigger value="interventions" data-testid="tab-interventions" className="text-xs sm:text-sm">Interventions ({interventions.length})</TabsTrigger>
            <TabsTrigger value="profil" data-testid="tab-profil" className="text-xs sm:text-sm">Mon Profil</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-sm sm:text-base font-semibold text-slate-900">Derniers devis</CardTitle></CardHeader>
              <CardContent>
                {devisList.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {devisList.slice(0, 5).map(d => (
                      <div key={d.devis_id} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => viewDevis(d.devis_id)} data-testid={`overview-devis-${d.devis_id}`}>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium truncate">{d.devis_number}</p>
                          <p className="text-[10px] sm:text-xs text-slate-500">{formatDate(d.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs sm:text-sm font-semibold">{d.total?.toLocaleString("fr-FR")} EUR</span>
                          <Badge className={`${devisStatusColors[d.status]} text-[9px] sm:text-[11px] font-medium border-0`}>{devisStatusLabels[d.status]}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">Aucun devis</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-sm sm:text-base font-semibold text-slate-900">Dernières factures</CardTitle></CardHeader>
              <CardContent>
                {factures.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {factures.slice(0, 5).map(f => (
                      <div key={f.facture_id} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => viewFacture(f)} data-testid={`overview-facture-${f.facture_id}`}>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium truncate">{f.facture_number}</p>
                          <p className="text-[10px] sm:text-xs text-slate-500">{formatDate(f.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs sm:text-sm font-semibold">{f.total?.toLocaleString("fr-FR")} EUR</span>
                          <Badge className={`${facStatusColors[f.status]} text-[9px] sm:text-[11px] font-medium border-0`}>{facStatusLabels[f.status]}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">Aucune facture</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="devis">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {devisList.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">Aucun devis trouvé</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">N° Devis</TableHead>
                        <TableHead className="text-xs font-semibold hidden sm:table-cell">Objet</TableHead>
                        <TableHead className="text-xs font-semibold hidden md:table-cell">Date</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                        <TableHead className="text-xs font-semibold">Statut</TableHead>
                        <TableHead className="text-xs font-semibold w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {devisList.map(d => (
                        <TableRow key={d.devis_id} data-testid={`client-devis-row-${d.devis_id}`}>
                          <TableCell className="text-xs sm:text-sm font-medium text-slate-900">{d.devis_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-600 max-w-[150px] truncate hidden sm:table-cell">{d.objet || "-"}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-500 hidden md:table-cell">{formatDate(d.created_at)}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-right font-semibold text-slate-900">{d.total?.toLocaleString("fr-FR")} EUR</TableCell>
                          <TableCell><Badge className={`${devisStatusColors[d.status]} text-[9px] sm:text-[11px] font-medium border-0`}>{devisStatusLabels[d.status]}</Badge></TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => viewDevis(d.devis_id)} className="h-7 w-7 p-0" data-testid={`view-devis-${d.devis_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commandes">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {commandes.length === 0 ? (
                <div className="p-12 text-center">
                  <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">Aucune commande</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">N° Commande</TableHead>
                        <TableHead className="text-xs font-semibold hidden sm:table-cell">N° Devis</TableHead>
                        <TableHead className="text-xs font-semibold hidden md:table-cell">Date</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                        <TableHead className="text-xs font-semibold">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commandes.map(c => (
                        <TableRow key={c.commande_id} data-testid={`client-cmd-row-${c.commande_id}`}>
                          <TableCell className="text-xs sm:text-sm font-medium text-slate-900">{c.commande_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-500 hidden sm:table-cell">{c.devis_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-500 hidden md:table-cell">{formatDate(c.created_at)}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-right font-semibold text-slate-900">{c.total?.toLocaleString("fr-FR")} EUR</TableCell>
                          <TableCell><Badge className={`${cmdStatusColors[c.status]} text-[9px] sm:text-[11px] font-medium border-0`}>{cmdStatusLabels[c.status]}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="factures">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {factures.length === 0 ? (
                <div className="p-12 text-center">
                  <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">Aucune facture</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">N° Facture</TableHead>
                        <TableHead className="text-xs font-semibold hidden sm:table-cell">N° Commande</TableHead>
                        <TableHead className="text-xs font-semibold hidden md:table-cell">Date</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                        <TableHead className="text-xs font-semibold">Statut</TableHead>
                        <TableHead className="text-xs font-semibold w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {factures.map(f => (
                        <TableRow key={f.facture_id} data-testid={`client-fac-row-${f.facture_id}`}>
                          <TableCell className="text-xs sm:text-sm font-medium text-slate-900">{f.facture_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-500 hidden sm:table-cell">{f.commande_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-slate-500 hidden md:table-cell">{formatDate(f.created_at)}</TableCell>
                          <TableCell className="text-xs sm:text-sm text-right font-semibold text-slate-900">{f.total?.toLocaleString("fr-FR")} EUR</TableCell>
                          <TableCell><Badge className={`${facStatusColors[f.status]} text-[9px] sm:text-[11px] font-medium border-0`}>{facStatusLabels[f.status]}</Badge></TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => viewFacture(f)} className="h-7 w-7 p-0" data-testid={`view-facture-client-${f.facture_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="interventions">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {interventions.length === 0 ? (
                <div className="p-12 text-center">
                  <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">Aucune intervention en cours</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {interventions.map(inv => (
                    <div key={inv.intervention_id} className="p-3 sm:p-4 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setSelectedIntervention(inv); setInterventionDialogOpen(true); }} data-testid={`client-intervention-${inv.intervention_id}`}>
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-900 truncate">{inv.title}</h3>
                        <Badge className={`${intStatusColors[inv.status]} text-[9px] sm:text-[11px] font-medium border-0 shrink-0`}>{intStatusLabels[inv.status]}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex justify-between text-[10px] sm:text-xs text-slate-400 mb-0.5">
                            <span>Progression</span>
                            <span>{inv.progress || 0}%</span>
                          </div>
                          <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${inv.progress || 0}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500 flex-wrap">
                        {inv.commande_number && <span>Cmd: {inv.commande_number}</span>}
                        {inv.location && <span>{inv.location}</span>}
                        {(inv.photos?.length || 0) > 0 && <span className="flex items-center gap-0.5"><Camera className="w-3 h-3" /> {inv.photos.length}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profil">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4" /> Mon Profil
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile ? (
                <div className="space-y-4 max-w-lg">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email</Label>
                    <Input value={profile.email} disabled className="bg-slate-50 text-sm" data-testid="profile-email" />
                    <p className="text-[10px] text-slate-400">L'email ne peut pas être modifié</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nom complet</Label>
                    <Input value={profileForm.name || ""} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} className="text-sm" data-testid="profile-name" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Société</Label>
                      <Input value={profileForm.company_name || ""} onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))} className="text-sm" data-testid="profile-company" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">SIRET</Label>
                      <Input value={profileForm.siret || ""} onChange={e => setProfileForm(p => ({ ...p, siret: e.target.value }))} className="text-sm" data-testid="profile-siret" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Téléphone</Label>
                    <Input value={profileForm.phone || ""} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} className="text-sm" data-testid="profile-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Adresse</Label>
                    <Input value={profileForm.address || ""} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))} className="text-sm" data-testid="profile-address" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Code postal</Label>
                      <Input value={profileForm.postal_code || ""} onChange={e => setProfileForm(p => ({ ...p, postal_code: e.target.value }))} className="text-sm" data-testid="profile-postal" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ville</Label>
                      <Input value={profileForm.city || ""} onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))} className="text-sm" data-testid="profile-city" />
                    </div>
                  </div>
                  <Button onClick={saveProfile} disabled={savingProfile} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="save-profile-btn">
                    <Save className="w-4 h-4" /> {savingProfile ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-slate-400">Chargement du profil...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Devis Detail Dialog */}
      <Dialog open={devisDialogOpen} onOpenChange={setDevisDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg sm:text-xl uppercase tracking-tight">Devis N° {selectedDevis?.devis?.devis_number}</DialogTitle>
            <DialogDescription>Détail de votre devis</DialogDescription>
          </DialogHeader>
          {selectedDevis && (
            <>
              <div id="devis-print-content">
                <div style={{background:"#1a3a5c",color:"white",padding:"16px",borderRadius:"8px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"8px",flexWrap:"wrap"}}>
                  <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                    <div style={{width:"50px",height:"50px",background:"white",borderRadius:"8px",padding:"3px",flexShrink:0}}>
                      <img src="/jvc-logo.png" alt="JVC" style={{width:"100%",height:"100%",objectFit:"contain"}} />
                    </div>
                    <div>
                      <div style={{fontSize:"14px",fontWeight:"bold",textTransform:"uppercase"}}>{selectedDevis.company?.company_name || "SAS J.V.C"}</div>
                      <div style={{fontSize:"10px",color:"#93c5fd",marginTop:"2px"}}>{selectedDevis.company?.activities}</div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:"18px",fontWeight:"bold",textTransform:"uppercase"}}>DEVIS</div>
                    <div style={{fontSize:"12px",color:"#bfdbfe"}}>{selectedDevis.devis.devis_number}</div>
                    <div style={{fontSize:"10px",color:"#93c5fd",marginTop:"4px"}}>Date: {formatDate(selectedDevis.devis.created_at)}</div>
                  </div>
                </div>

                <div style={{padding:"16px 0"}}>
                  {selectedDevis.devis.objet && (
                    <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",padding:"8px 12px",borderRadius:"6px",marginBottom:"12px"}}>
                      <div style={{fontSize:"9px",color:"#3b82f6",textTransform:"uppercase",fontWeight:"600"}}>Objet</div>
                      <div style={{fontSize:"12px",fontWeight:"500",marginTop:"2px"}}>{selectedDevis.devis.objet}</div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#1a3a5c]">
                          <TableHead className="text-white text-xs font-semibold">Description</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-center">Qté</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-right">P.U.</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDevis.devis.items?.map((item, idx) => (
                          item.item_type === "title" ? (
                            <TableRow key={idx} className="bg-[#1a3a5c]">
                              <TableCell colSpan={4} className="text-white font-bold text-xs sm:text-sm uppercase py-2">{item.description}</TableCell>
                            </TableRow>
                          ) : (
                            <TableRow key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                              <TableCell className="text-xs sm:text-sm">{item.description}</TableCell>
                              <TableCell className="text-xs sm:text-sm text-center">{item.quantity} {item.unit}</TableCell>
                              <TableCell className="text-xs sm:text-sm text-right">{item.unit_price?.toFixed(2)}</TableCell>
                              <TableCell className="text-xs sm:text-sm text-right font-medium">{item.total?.toFixed(2)} EUR</TableCell>
                            </TableRow>
                          )
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:"12px"}}>
                    <div style={{width:"220px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:"12px"}}>
                        <span style={{color:"#64748b"}}>Sous-total</span>
                        <span style={{fontWeight:"500"}}>{selectedDevis.devis.subtotal?.toFixed(2)} EUR</span>
                      </div>
                      {selectedDevis.devis.tax_rate > 0 && (
                        <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:"12px"}}>
                          <span style={{color:"#64748b"}}>TVA ({selectedDevis.devis.tax_rate}%)</span>
                          <span style={{fontWeight:"500"}}>{selectedDevis.devis.tax_amount?.toFixed(2)} EUR</span>
                        </div>
                      )}
                      <div style={{background:"#1a3a5c",color:"white",padding:"8px 12px",borderRadius:"6px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"6px"}}>
                        <span style={{fontWeight:"600",fontSize:"12px"}}>TOTAL</span>
                        <span style={{fontSize:"16px",fontWeight:"bold"}}>{selectedDevis.devis.total?.toFixed(2)} EUR</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={() => printDocument("devis-print-content", `Devis ${selectedDevis.devis.devis_number}`)} variant="outline" size="sm" data-testid="print-devis-btn">
                  <Printer className="w-4 h-4 mr-1.5" /> Imprimer
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Facture Detail Dialog */}
      <Dialog open={factureDialogOpen} onOpenChange={setFactureDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg sm:text-xl uppercase tracking-tight">Facture N° {selectedFacture?.facture_number}</DialogTitle>
            <DialogDescription>Commande {selectedFacture?.commande_number}</DialogDescription>
          </DialogHeader>
          {selectedFacture && (
            <>
              <div id="facture-client-print-content">
                <div style={{background:"#1a3a5c",color:"white",padding:"16px",borderRadius:"8px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"8px",flexWrap:"wrap"}}>
                  <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                    <div style={{width:"50px",height:"50px",background:"white",borderRadius:"8px",padding:"3px",flexShrink:0}}>
                      <img src="/jvc-logo.png" alt="JVC" style={{width:"100%",height:"100%",objectFit:"contain"}} />
                    </div>
                    <div>
                      <div style={{fontSize:"14px",fontWeight:"bold",textTransform:"uppercase"}}>{selectedFacture.company?.name || "SAS J.V.C"}</div>
                      <div style={{fontSize:"10px",color:"#93c5fd"}}>{selectedFacture.company?.activities}</div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:"18px",fontWeight:"bold",textTransform:"uppercase"}}>FACTURE</div>
                    <div style={{fontSize:"12px",color:"#bfdbfe"}}>{selectedFacture.facture_number}</div>
                    <div style={{fontSize:"10px",color:"#93c5fd",marginTop:"4px"}}>Date: {formatDate(selectedFacture.created_at)}</div>
                  </div>
                </div>

                <div style={{padding:"16px 0"}}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#1a3a5c]">
                          <TableHead className="text-white text-xs font-semibold">Description</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-center">Qté</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-right">P.U.</TableHead>
                          <TableHead className="text-white text-xs font-semibold text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedFacture.items?.map((item, idx) => (
                          <TableRow key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <TableCell className="text-xs sm:text-sm">{item.description}</TableCell>
                            <TableCell className="text-xs sm:text-sm text-center">{item.quantity} {item.unit}</TableCell>
                            <TableCell className="text-xs sm:text-sm text-right">{item.unit_price?.toFixed(2)}</TableCell>
                            <TableCell className="text-xs sm:text-sm text-right font-medium">{item.total?.toFixed(2)} EUR</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:"12px"}}>
                    <div style={{width:"220px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:"12px"}}>
                        <span style={{color:"#64748b"}}>Total net</span>
                        <span style={{fontWeight:"500"}}>{selectedFacture.subtotal?.toFixed(2)} EUR</span>
                      </div>
                      {selectedFacture.tax_rate > 0 && (
                        <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:"12px"}}>
                          <span style={{color:"#64748b"}}>TVA ({selectedFacture.tax_rate}%)</span>
                          <span style={{fontWeight:"500"}}>{selectedFacture.tax_amount?.toFixed(2)} EUR</span>
                        </div>
                      )}
                      <div style={{background:"#1a3a5c",color:"white",padding:"8px 12px",borderRadius:"6px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"6px"}}>
                        <span style={{fontWeight:"600",fontSize:"12px"}}>NET A PAYER</span>
                        <span style={{fontSize:"16px",fontWeight:"bold"}}>{selectedFacture.total?.toFixed(2)} EUR</span>
                      </div>
                    </div>
                  </div>

                  {selectedFacture.company?.iban && (
                    <div style={{background:"#f8fafc",padding:"10px",borderRadius:"6px",marginTop:"16px",fontSize:"11px"}}>
                      <div style={{fontSize:"9px",color:"#94a3b8",textTransform:"uppercase",fontWeight:"600",marginBottom:"4px"}}>Coordonnées bancaires</div>
                      <div style={{color:"#475569"}}><strong>IBAN:</strong> {selectedFacture.company.iban}</div>
                      <div style={{color:"#475569"}}><strong>BIC:</strong> {selectedFacture.company.bic}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={() => printDocument("facture-client-print-content", `Facture ${selectedFacture.facture_number}`)} variant="outline" size="sm" data-testid="print-facture-client-btn">
                  <Printer className="w-4 h-4 mr-1.5" /> Imprimer
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Intervention Detail Dialog */}
      <Dialog open={interventionDialogOpen} onOpenChange={setInterventionDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg sm:text-xl uppercase tracking-tight">{selectedIntervention?.title}</DialogTitle>
            <DialogDescription>
              {selectedIntervention?.commande_number && `Commande: ${selectedIntervention.commande_number}`}
            </DialogDescription>
          </DialogHeader>
          {selectedIntervention && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={`${intStatusColors[selectedIntervention.status]} border-0 px-2.5 py-0.5`}>
                  {intStatusLabels[selectedIntervention.status]}
                </Badge>
                <div className="flex-1">
                  <div className="flex justify-between text-[10px] sm:text-xs text-slate-400 mb-0.5">
                    <span>Progression</span>
                    <span>{selectedIntervention.progress || 0}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${selectedIntervention.progress || 0}%` }} />
                  </div>
                </div>
              </div>

              {selectedIntervention.description && (
                <div className="text-xs sm:text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
                  <p className="text-[10px] sm:text-xs text-slate-400 mb-1">Description</p>
                  {selectedIntervention.description}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                {selectedIntervention.location && (
                  <div className="bg-slate-50 p-2.5 rounded-md">
                    <p className="text-[10px] sm:text-xs text-slate-400">Lieu</p>
                    <p className="font-medium">{selectedIntervention.location}</p>
                  </div>
                )}
                {selectedIntervention.assigned_to && (
                  <div className="bg-slate-50 p-2.5 rounded-md">
                    <p className="text-[10px] sm:text-xs text-slate-400">Technicien</p>
                    <p className="font-medium">{selectedIntervention.assigned_to}</p>
                  </div>
                )}
              </div>

              {selectedIntervention.photos?.length > 0 && (
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                    <Camera className="w-3.5 h-3.5" /> Photos ({selectedIntervention.photos.length})
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedIntervention.photos.map((url, idx) => (
                      <a key={idx} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${BACKEND_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-20 sm:h-24 object-cover rounded-md border border-slate-200 hover:opacity-80 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedIntervention.comments?.length > 0 && (
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                    <MessageSquare className="w-3.5 h-3.5" /> Commentaires ({selectedIntervention.comments.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedIntervention.comments.map((c, idx) => (
                      <div key={idx} className="p-2.5 bg-slate-50 rounded-md text-xs sm:text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-[10px] sm:text-xs text-slate-700">{c.author}</span>
                          <span className="text-[9px] sm:text-[10px] text-slate-400">{formatDate(c.created_at)}</span>
                        </div>
                        <p className="text-slate-600">{c.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
